import { useEffect, useState } from 'react';

/** Phones: 640px and narrower. */
export const MOBILE_QUERY = '(max-width: 640px)';

/**
 * Mobile column priority: Status | RTU name | Well name + Well ID | actions overflow button.
 * Every other column is off by default on a phone; the column picker can still enable it.
 */
export const MOBILE_DEFAULT_COLUMNS: readonly string[] = ['status', 'name', 'wellName', 'wellId'];

export const isMobileViewport = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;

/** Applies the mobile defaults on a phone and leaves the desktop defaults untouched. A persisted choice is applied afterwards and wins. */
export const withMobileColumnDefaults = <T extends { field: string; visible: boolean }>(columns: T[]): T[] =>
  isMobileViewport()
    ? columns.map((column) => ({ ...column, visible: MOBILE_DEFAULT_COLUMNS.includes(column.field) }))
    : columns;

/** True while the viewport is a phone; follows rotation / resize. */
export const useIsMobileViewport = (): boolean => {
  const [mobile, setMobile] = useState(isMobileViewport);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(MOBILE_QUERY);
    const listener = () => setMobile(query.matches);
    listener();
    if (query.addEventListener) {
      query.addEventListener('change', listener);
      return () => query.removeEventListener('change', listener);
    }
    return undefined;
  }, []);
  return mobile;
};

/**
 * Persists only the columns the user toggled, merged into what is already stored. Defaults (desktop or mobile) are never
 * written as if they were a user choice, so a phone session cannot hide columns on the desktop and the reverse.
 */
export const persistToggledColumns = (
  columns: Array<{ field: string; visible: boolean }>,
  touched: Set<string>,
  storageKey: string
): void => {
  if (typeof window === 'undefined' || touched.size === 0) {
    return;
  }
  try {
    let stored: Record<string, boolean> = {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        stored = parsed as Record<string, boolean>;
      }
    } catch {
      stored = {};
    }
    touched.forEach((field) => {
      const column = columns.find((item) => item.field === field);
      if (column) {
        stored[field] = Boolean(column.visible);
      }
    });
    window.localStorage.setItem(storageKey, JSON.stringify(stored));
  } catch (error) {
    console.warn('Failed to persist column visibility settings', error);
  }
};
